import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/context/CompanyContext';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useNotifications } from '@/hooks/useNotifications';
import { useManagementMode } from '@/hooks/useManagementMode';
import { useSecureWithdrawal } from '@/hooks/useSecureWithdrawal';
import { useToast } from '@/components/ui/use-toast';
import { CaisseLayout } from '@/components/layouts/CaisseLayout';
import { QrScanner } from '@/components/common/QrScanner';
import { RetraitTotalBubble } from '@/components/caisse/RetraitTotalBubble';
import { SearchBar } from '@/components/inventory/SearchBar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Check, Trash2, ShoppingCart, Search, X, Menu, Lock, Minus, Package, RotateCcw, Scan, ChevronDown, ChevronUp } from 'lucide-react';
import { Product } from '@/context/SupabaseInventoryContext';

export const Retrait: React.FC<{
  sidebarTrigger?: React.ReactNode;
}> = ({
  sidebarTrigger
}) => {
  const navigate = useNavigate();
  const {
    currentCompany,
    userRole
  } = useCompany();
  const {
    products,
    visibleProducts,
    hiddenProducts,
    refreshProducts
  } = useSupabaseInventory();
  const {
    hasAccess
  } = useSubscription();
  const {
    success,
    error
  } = useNotifications();
  
  // Secure withdrawal hook with backend validation
  const {
    cart,
    isProcessing: isProcessingWithdrawal,
    addToCart: secureAddToCart,
    updateQuantity: secureUpdateQuantity,
    removeFromCart: secureRemoveFromCart,
    clearCart: secureClearCart,
    processWithdrawal: secureProcessWithdrawal
  } = useSecureWithdrawal(currentCompany?.id);
  const { isCaisseMode, loading: modeLoading } = useManagementMode();
  const {
    toast
  } = useToast();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState<'all' | 'name' | 'barcode'>('all');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dailySearchCount, setDailySearchCount] = useState(0);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const canAccess = userRole === 'admin' || userRole === 'member' || userRole === 'vendeur';

  // Vérifier le mode de gestion et rediriger si nécessaire
  useEffect(() => {
    if (!modeLoading && isCaisseMode) {
      navigate('/caisse', { replace: true });
    }
  }, [isCaisseMode, modeLoading, navigate]);

  // Load daily search count from localStorage
  useEffect(() => {
    const today = new Date().toDateString();
    const storedData = localStorage.getItem('retraitSearchData');
    if (storedData) {
      const {
        date,
        count
      } = JSON.parse(storedData);
      if (date === today) {
        setDailySearchCount(count);
      } else {
        // Reset count for new day
        localStorage.setItem('retraitSearchData', JSON.stringify({
          date: today,
          count: 0
        }));
        setDailySearchCount(0);
      }
    } else {
      localStorage.setItem('retraitSearchData', JSON.stringify({
        date: today,
        count: 0
      }));
    }
  }, []);

  // Vérification des permissions
  if (!canAccess) {
    return <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 sm:p-8 text-center max-w-md w-full">
          <Package className="w-12 h-12 sm:w-16 sm:h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold mb-2">Accès non autorisé</h2>
          <p className="text-muted-foreground text-sm sm:text-base">Vous n'avez pas les permissions pour accéder au retrait.</p>
        </Card>
      </div>;
  }

  const handleBarcodeScanned = (barcode: string) => {
    // Check search limit only for demo users who are company owners (not invited members)
    if (!hasAccess && userRole === 'admin') {
      if (dailySearchCount >= 15) {
        toast({
          title: "Limite de recherche atteinte",
          description: "Vous avez atteint la limite de 15 recherches par jour. Passez à Premium pour un accès illimité.",
          variant: "destructive"
        });
        return;
      }

      // Increment search count
      const newCount = dailySearchCount + 1;
      setDailySearchCount(newCount);

      // Save to localStorage
      const today = new Date().toDateString();
      localStorage.setItem('retraitSearchData', JSON.stringify({
        date: today,
        count: newCount
      }));
    }
    const normalizedBarcode = barcode.trim();
    const product = products.find(p => p.barcode === normalizedBarcode);
    if (product) {
      // Ajout automatique au panier lors du scan ou saisie manuelle
      addProductDirectly(product);
    } else {
      error({
        title: "Produit non trouvé",
        description: `Aucun produit trouvé avec le code-barres: ${normalizedBarcode}`
      });
    }
  };

  // Recherche en temps réel
  const [filteredVisibleProducts, setFilteredVisibleProducts] = useState<Product[]>([]);
  const [filteredHiddenProducts, setFilteredHiddenProducts] = useState<Product[]>([]);
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredVisibleProducts([]);
      setFilteredHiddenProducts([]);
      return;
    }
    const filterProducts = (productList: Product[]) => {
      const query = searchQuery.toLowerCase();
      return productList.filter(product => {
        switch (searchFilter) {
          case 'name':
            return product.name.toLowerCase().includes(query);
          case 'barcode':
            return product.barcode?.toLowerCase().includes(query);
          default:
            return product.name.toLowerCase().includes(query) || product.barcode?.toLowerCase().includes(query);
        }
      });
    };
    setFilteredVisibleProducts(filterProducts(visibleProducts).slice(0, 10));
    setFilteredHiddenProducts(filterProducts(hiddenProducts).slice(0, 5));
  }, [searchQuery, visibleProducts, hiddenProducts, searchFilter]);

  // Combine visible and hidden for display
  const filteredProducts = [...filteredVisibleProducts, ...filteredHiddenProducts];
  
  const addProductDirectly = async (product: Product) => {
    if (product.quantity === 0) {
      error({
        title: "Stock épuisé",
        description: `Le produit ${product.name} n'est plus en stock.`
      });
      return;
    }

    // Use secure backend add to cart
    const addSuccess = await secureAddToCart(product, 1);

    if (addSuccess) {
      // Vider le champ de recherche seulement si c'est un code-barres
      if (/^\d{8,13}$/.test(searchQuery.trim())) {
        setSearchQuery('');
      }
    }
  };

  const removeFromCart = async (productId: string) => {
    await secureRemoveFromCart(productId);
  };

  const updateCartQuantity = async (productId: string, newQuantity: number) => {
    await secureUpdateQuantity(productId, newQuantity);
  };

  const clearCart = () => {
    secureClearCart();
  };

  const validateRetrait = () => {
    if (cart.length === 0) {
      error({
        title: "Panier vide",
        description: "Ajoutez des produits avant de valider le retrait."
      });
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const confirmRetrait = async () => {
    if (isProcessingWithdrawal || isProcessing) return;
    setIsProcessing(true);
    
    try {
      // Use secure backend withdrawal processing
      const withdrawalSuccess = await secureProcessWithdrawal('Retrait stock');
      
      if (withdrawalSuccess) {
        await refreshProducts();
        setIsConfirmModalOpen(false);
      }
    } catch (err) {
      error({
        title: "Erreur lors du retrait",
        description: "Impossible de traiter le retrait. Veuillez réessayer."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const retraitActions = <div className="flex items-center gap-2 sm:gap-3">
      <Badge variant="outline" className="px-2 sm:px-3 py-1 text-xs sm:text-sm">
        {cart.length} article{cart.length > 1 ? 's' : ''}
      </Badge>
    </div>;

  // Composant Panier réutilisable
  const CartComponent = ({
    className = ""
  }: {
    className?: string;
  }) => {
    return (
      <Card className={`sticky top-4 z-10 ${className}`}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
            Retrait {retraitActions}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {cart.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Aucun article à retirer</p>
            ) : (
              <>
                <div className="space-y-2 max-h-64 sm:max-h-80 lg:max-h-96 xl:max-h-[500px] overflow-y-auto pb-32 sm:pb-0">
                  {cart.map(item => (
                    <div key={item.product.id} className="flex items-center justify-between p-2 border rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.product.name}</p>
                        <div className="text-xs text-muted-foreground">
                          <span>Qté: {item.quantity}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                          className="h-6 w-6 p-0"
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="mx-2 text-sm font-medium w-8 text-center">{item.quantity}</span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                          className="h-6 w-6 p-0"
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={() => removeFromCart(item.product.id)}
                          className="h-6 w-6 p-0 ml-1"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Section fixe en bas - Mobile uniquement */}
                <div className="fixed bottom-0 left-0 right-0 bg-card border-t p-4 shadow-lg z-20 xl:hidden">
                  <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={clearCart}
                  >
                    Vider
                  </Button>
                    <Button 
                      className="flex-1" 
                      onClick={() => setIsConfirmModalOpen(true)}
                      disabled={cart.length === 0}
                    >
                      Valider Retrait
                    </Button>
                  </div>
                </div>
                
                {/* Section boutons - Desktop uniquement */}
                <div className="hidden xl:flex gap-2 pt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1" 
                      onClick={clearCart}
                    >
                      Vider
                    </Button>
                  <Button 
                    className="flex-1" 
                    onClick={() => setIsConfirmModalOpen(true)}
                    disabled={cart.length === 0}
                  >
                    Valider Retrait
                  </Button>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return <div className="retrait-theme">
    <CaisseLayout title="Retrait de Stock" actions={retraitActions} sidebarTrigger={sidebarTrigger}>
      {/* Bubble total qui suit le scroll */}
      <RetraitTotalBubble 
        itemCount={cart.length}
        isVisible={cart.length > 0}
      />

      <div className="container mx-auto max-w-full px-4 sm:px-6">
        {/* Layout Mobile-First avec stacking vertical */}
        <div className="flex flex-col xl:flex-row gap-4 sm:gap-6">
          {/* Section Scanner/Recherche - Mobile: pleine largeur, Desktop: 2/3 largeur */}
          <div className="flex-1 xl:flex-[2] space-y-4 sm:space-y-6">
            
            {/* Scanner Section */}
            <Card className="hover-lift border-l-4 border-l-orange-500">
              <CardHeader className="pb-6">
                <CardTitle className="flex items-center gap-2 text-lg text-orange-700 dark:text-orange-400">
                  <Scan className="w-5 h-5" />
                  Scanner / Rechercher un produit
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">RETRAIT</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Section de recherche */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="relative">
                    <Input placeholder="Rechercher un produit ou saisir code-barres..." value={searchQuery} onChange={e => {
                    const value = e.target.value;
                    setSearchQuery(value);
                  }} onKeyPress={e => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      handleBarcodeScanned(searchQuery.trim());
                    }
                  }} disabled={!hasAccess && userRole === 'admin' && dailySearchCount >= 15} className={`w-full h-10 sm:h-11 text-sm sm:text-base ${searchQuery ? "pr-16 sm:pr-20" : ""}`} />
                    {searchQuery && <Button variant="outline" size="sm" onClick={handleClearSearch} className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 h-7 sm:h-8 text-xs px-2 sm:px-3">
                        <span className="hidden sm:inline">Effacer</span>
                        <span className="sm:hidden">✕</span>
                      </Button>}
                  </div>
                  {!hasAccess && userRole === 'admin' && <div className="text-xs text-muted-foreground px-1">
                      Mode démo: Recherches utilisées: {dailySearchCount}/15 par jour
                    </div>}
                </div>

                {/* Section scanner QR */}
                <div className="border-t pt-4 sm:pt-6">
                  <QrScanner onResult={handleBarcodeScanned} className="w-full" />
                </div>

                {/* Résultats de recherche */}
                {filteredProducts.length > 0 && <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}:
                    </p>
                    <div className="grid gap-2">
                      {filteredProducts.map(product => {
                    const isHidden = hiddenProducts.includes(product);
                    return <div key={product.id} className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${isHidden ? 'blur-sm opacity-50 pointer-events-none cursor-not-allowed bg-muted/50' : 'hover:bg-accent cursor-pointer'}`} onClick={() => !isHidden && addProductDirectly(product)}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm sm:text-base truncate">{product.name}</span>
                                {isHidden && <Badge variant="outline" className="text-xs">
                                    Premium requis
                                  </Badge>}
                                {!isHidden && product.quantity <= product.min_threshold && <Badge variant="destructive" className="text-xs">
                                    Stock faible
                                  </Badge>}
                              </div>
                              <div className="text-xs sm:text-sm text-muted-foreground space-x-2">
                                <span>Stock: {product.quantity}</span>
                                {product.barcode && <span>Code: {product.barcode}</span>}
                              </div>
                            </div>
                            <Button size="sm" className="ml-2 h-8 px-3 text-xs" disabled={isHidden} onClick={e => {
                        e.stopPropagation();
                        if (!isHidden) addProductDirectly(product);
                      }}>
                              {isHidden ? 'Bloqué' : 'Ajouter'}
                            </Button>
                          </div>;
                  })}
                    </div>
                    {filteredHiddenProducts.length > 0 && <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800 mb-2">
                          {filteredHiddenProducts.length} produit{filteredHiddenProducts.length > 1 ? 's' : ''} supplémentaire{filteredHiddenProducts.length > 1 ? 's' : ''} disponible{filteredHiddenProducts.length > 1 ? 's' : ''} avec Premium
                        </p>
                        <p className="text-xs text-amber-600">
                          Passez à Premium pour débloquer tous vos produits
                        </p>
                      </div>}
                  </div>}
              </CardContent>
            </Card>

            {/* Panier - Version mobile uniquement */}
            <div className="xl:hidden">
              <CartComponent />
            </div>

            {/* Produits populaires */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                    Tous les produits ({visibleProducts.length})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowAllProducts(!showAllProducts)} className="flex items-center gap-1 h-8 px-2">
                      {showAllProducts ? <>
                          <ChevronUp className="w-4 h-4" />
                          <span className="text-xs">Moins</span>
                        </> : <>
                          <ChevronDown className="w-4 h-4" />
                          <span className="text-xs">Plus</span>
                        </>}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
                  {(showAllProducts ? visibleProducts : visibleProducts.slice(0, 8)).map(product => {
                    const isInCart = cart.some(item => item.product.id === product.id);
                    return <div 
                      key={product.id} 
                      className={`flex flex-col p-3 border rounded-lg hover:bg-accent cursor-pointer transition-all ${isInCart ? 'border-orange-500 border-2' : ''}`}
                      onClick={() => addProductDirectly(product)}
                    >
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-24 object-cover rounded-md mb-2"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 flex flex-col">
                        <p className="font-medium text-sm truncate mb-1">{product.name}</p>
                        <div className="text-xs text-muted-foreground mb-2 space-y-1">
                          <div>Stock: {product.quantity}</div>
                          {product.quantity <= product.min_threshold && (
                            <Badge variant="destructive" className="text-xs">
                              Stock faible
                            </Badge>
                          )}
                        </div>
                        <Button size="sm" className="w-full h-7 text-xs mt-auto" onClick={e => {
                          e.stopPropagation();
                          addProductDirectly(product);
                        }}>
                          Ajouter
                        </Button>
                      </div>
                    </div>;
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Section Panier - Mobile: pleine largeur, Desktop: 1/3 largeur */}
          <div className="hidden xl:block xl:flex-1">
            <CartComponent />
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={isConfirmModalOpen} onOpenChange={setIsConfirmModalOpen}>
        <DialogContent className="max-w-md w-[95vw] mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5" />
              Confirmer le retrait
            </DialogTitle>
            <DialogDescription className="text-sm">
              Récapitulatif des produits à retirer du stock
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Articles */}
            <div className="space-y-2">
              {cart.map(item => <div key={item.product.id} className="flex justify-between items-center text-sm">
                  <span className="truncate flex-1 mr-2">{item.product.name}</span>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>Qté: {item.quantity}</span>
                  </div>
                </div>)}
            </div>
          </div>

          <DialogFooter className="flex-col space-y-2 sm:space-y-0 sm:flex-row">
            <Button onClick={confirmRetrait} disabled={isProcessing} className="w-full">
              {isProcessing ? 'Traitement...' : 'Confirmer le retrait'}
            </Button>
            <Button variant="outline" onClick={() => setIsConfirmModalOpen(false)} disabled={isProcessing} className="w-full sm:w-auto">
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CaisseLayout>
  </div>;
};